import React, { useState, useEffect, useCallback } from "react";
import api from "../../utils/api";
import { BarChart2, TrendingUp, TrendingDown, DollarSign, AlertCircle, RefreshCw } from "lucide-react";

const fmt = (n) => `A$${Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function toMonthKey(ts) {
  if (!ts) return null;
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function toMonthLabel(key) {
  if (!key) return "";
  const [y, m] = key.split("-");
  return `${MONTHS[parseInt(m, 10) - 1]} ${y}`;
}

export default function FinancialReports() {
  const [invoices, setInvoices] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [period, setPeriod] = useState("6m");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [invRes, poRes] = await Promise.all([
        api.get("/api/payments/invoices"),
        api.get("/api/purchase-orders"),
      ]);
      setInvoices(invRes.data.invoices || []);
      setPurchaseOrders(poRes.data.orders || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const now = new Date();
  const monthsBack = period === "3m" ? 3 : period === "6m" ? 6 : period === "12m" ? 12 : 1000;

  const periodStart = new Date(now);
  periodStart.setMonth(periodStart.getMonth() - monthsBack);

  const inPeriod = (ts) => {
    if (!ts) return false;
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d >= periodStart;
  };

  const filteredInvoices = invoices.filter(inv => inPeriod(inv.createdAt));
  const filteredPOs = purchaseOrders.filter(po => inPeriod(po.createdAt) && po.status !== "cancelled");

  const totalRevenue = filteredInvoices.reduce((s, inv) => s + (parseFloat(inv.totalAmount) || 0), 0);
  const totalExpenses = filteredPOs.reduce((s, po) => s + (parseFloat(po.subtotal) || 0), 0);
  const netProfit = totalRevenue - totalExpenses;

  const paidInvoices = filteredInvoices.filter(inv => inv.status === "paid");
  const unpaidInvoices = filteredInvoices.filter(inv => inv.status !== "paid" && inv.status !== "cancelled");
  const totalPaid = paidInvoices.reduce((s, inv) => s + (parseFloat(inv.totalAmount) || 0), 0);
  const totalUnpaid = unpaidInvoices.reduce((s, inv) => s + (parseFloat(inv.totalAmount) || 0), 0);

  // Build monthly breakdown
  const monthlyMap = {};
  filteredInvoices.forEach(inv => {
    const k = toMonthKey(inv.createdAt);
    if (!k) return;
    if (!monthlyMap[k]) monthlyMap[k] = { revenue: 0, expenses: 0 };
    monthlyMap[k].revenue += parseFloat(inv.totalAmount) || 0;
  });
  filteredPOs.forEach(po => {
    const k = toMonthKey(po.createdAt);
    if (!k) return;
    if (!monthlyMap[k]) monthlyMap[k] = { revenue: 0, expenses: 0 };
    monthlyMap[k].expenses += parseFloat(po.subtotal) || 0;
  });

  const sortedMonths = Object.keys(monthlyMap).sort();
  const maxVal = Math.max(...sortedMonths.map(k => Math.max(monthlyMap[k].revenue, monthlyMap[k].expenses)), 1);

  // Top suppliers by PO spend
  const supplierSpend = {};
  filteredPOs.forEach(po => {
    if (!po.supplierName) return;
    supplierSpend[po.supplierName] = (supplierSpend[po.supplierName] || 0) + (parseFloat(po.subtotal) || 0);
  });
  const topSuppliers = Object.entries(supplierSpend).sort((a, b) => b[1] - a[1]).slice(0, 5);

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-emerald-100 rounded-xl"><BarChart2 size={24} className="text-emerald-600"/></div>
          <div>
            <h1 className="text-2xl font-bold text-navy-900">Financial Reports</h1>
            <p className="text-slate-500 text-sm">Revenue, expenses and profitability overview</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {["3m","6m","12m","all"].map(p => (
            <button key={p} onClick={() => setPeriod(p)}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-colors ${period === p ? "bg-navy-900 text-white" : "bg-white border border-slate-200 text-slate-500 hover:border-slate-300"}`}>
              {p === "all" ? "All time" : `Last ${p}`}
            </button>
          ))}
          <button onClick={load} className="p-2 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-400">
            <RefreshCw size={15}/>
          </button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2"><AlertCircle size={16}/>{error}</div>}

      {loading ? (
        <div className="text-center py-16 text-slate-400 text-sm">Loading financial data...</div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: "Total Revenue",   value: fmt(totalRevenue),  icon: <TrendingUp size={18} className="text-emerald-500"/>, cls: "border-emerald-100" },
              { label: "Total Expenses",  value: fmt(totalExpenses), icon: <TrendingDown size={18} className="text-red-400"/>,   cls: "border-red-100" },
              { label: "Net Profit",      value: fmt(netProfit),     icon: <DollarSign size={18} className={netProfit >= 0 ? "text-emerald-500" : "text-red-500"}/>, cls: netProfit >= 0 ? "border-emerald-100" : "border-red-100" },
              { label: "Outstanding AR",  value: fmt(totalUnpaid),   icon: <AlertCircle size={18} className="text-amber-500"/>, cls: "border-amber-100" },
            ].map((m, i) => (
              <div key={i} className={`bg-white rounded-2xl border shadow-sm p-5 ${m.cls}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{m.label}</span>
                  {m.icon}
                </div>
                <p className="text-xl font-bold text-navy-900">{m.value}</p>
              </div>
            ))}
          </div>

          {/* Invoice Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <h3 className="font-bold text-navy-900 mb-4">Accounts Receivable</h3>
              <div className="space-y-3">
                {[
                  { label: "Paid invoices",    count: paidInvoices.length,   amount: totalPaid,   cls: "text-emerald-600" },
                  { label: "Unpaid invoices",  count: unpaidInvoices.length, amount: totalUnpaid, cls: "text-amber-600" },
                  { label: "Total invoices",   count: filteredInvoices.length, amount: totalRevenue, cls: "text-navy-900" },
                ].map((row, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                    <span className="text-sm text-slate-600">{row.label} <span className="text-slate-400">({row.count})</span></span>
                    <span className={`font-bold text-sm ${row.cls}`}>{fmt(row.amount)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <h3 className="font-bold text-navy-900 mb-4">Top Suppliers by Spend</h3>
              {topSuppliers.length === 0 ? (
                <p className="text-sm text-slate-400">No purchase orders in this period.</p>
              ) : (
                <div className="space-y-3">
                  {topSuppliers.map(([name, amount], i) => {
                    const pct = totalExpenses > 0 ? (amount / totalExpenses) * 100 : 0;
                    return (
                      <div key={i}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-slate-700 truncate max-w-[160px]">{name}</span>
                          <span className="font-semibold text-navy-900">{fmt(amount)}</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full">
                          <div className="h-1.5 bg-violet-400 rounded-full" style={{ width: `${pct}%` }}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Monthly Bar Chart */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <h3 className="font-bold text-navy-900 mb-6">Monthly Revenue vs Expenses</h3>
            {sortedMonths.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">No data for selected period.</p>
            ) : (
              <div className="flex items-end gap-3 h-48 overflow-x-auto pb-2">
                {sortedMonths.map(k => {
                  const { revenue, expenses } = monthlyMap[k];
                  const revH = maxVal > 0 ? (revenue / maxVal) * 160 : 0;
                  const expH = maxVal > 0 ? (expenses / maxVal) * 160 : 0;
                  return (
                    <div key={k} className="flex flex-col items-center gap-1 min-w-[56px]">
                      <div className="flex items-end gap-1 h-40">
                        <div title={`Revenue: ${fmt(revenue)}`}
                          className="w-5 bg-emerald-400 rounded-t-sm transition-all hover:bg-emerald-500"
                          style={{ height: `${Math.max(revH, 2)}px` }}/>
                        <div title={`Expenses: ${fmt(expenses)}`}
                          className="w-5 bg-red-300 rounded-t-sm transition-all hover:bg-red-400"
                          style={{ height: `${Math.max(expH, 2)}px` }}/>
                      </div>
                      <span className="text-[9px] text-slate-400 font-medium">{toMonthLabel(k)}</span>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex items-center gap-4 mt-4 text-xs text-slate-500">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-emerald-400 rounded-sm inline-block"/>Revenue</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-red-300 rounded-sm inline-block"/>Expenses</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
